import React, {useEffect, useState} from "react";
import Page from "../../Components/Page";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import {useParams} from "react-router-dom";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import {getStringParamOrDefault, updateQueryStringForState} from "../../../utils/urlBuilder";
import moment from "moment";
import {todayChicago} from "../../Alerts/MissingData/filters";
import ReactTable from "../../Components/ReactTable";
import CsvDownload from "../../../utils/CsvDownloadWithNulls";
import DateRangeInput from "../../Components/DateRangePicker";
import {makeApiCall} from "../../../Api/makeApiCall";
import _ from "lodash";
import {NUMBER_EXPRESSION_COLUMN} from "../../Projections/Components/storeUniverseTableHelpers";

function ClientDashboard() {
    const params = useParams();
    const brandId = parseInt(params.brandId);
    const brandName = getStringParamOrDefault('brandName');
    const today = todayChicago()
    const yesterday = moment(today).subtract(1, 'days').format('YYYY-MM-DD');

    const [startDate, setStartDate] = useState(getStringParamOrDefault('startDate', yesterday));
    const [endDate, setEndDate] = useState(getStringParamOrDefault('endDate', yesterday));
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);


    const getData = () => {
        setLoading(true)
        makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/clientDashboard?brand_id=${brandId}&start_date=${startDate}&end_date=${endDate}`)
            .then(data => setData(data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setData([])
    }, [startDate, endDate]);

    useEffect(() => {
        getData();
    }, []);

    const handleDateChange = ({ fromFormatted, toFormatted }) => {
        setStartDate(fromFormatted || null);
        setEndDate(toFormatted || null);
        updateQueryStringForState({ startDate: fromFormatted, endDate: toFormatted });
    };

    const distributor_helper = (data, statusKey) => {
        const statusData = data?.distributor_status?.[0]?.[statusKey]?.[0] ?? null;
        return statusData ? `${statusData?.actionDecode}: ${statusData?.contactDate}` : "";
    };

    const customColumns = [
        {
            header: "Merchant Name",
            filterable: true,
            accessorKey: "merchant_name",
            sticky: "left",
            cell: ({row}) => <a
                href={"/merchantDetails/" + row.original.merchant_id + "?brandId=" + brandId + "&brandName=" + brandName + "&merchantName=" + row.original.merchant_name + "&startDate=" + startDate + "&endDate=" + endDate}
                rel="noopener noreferrer">{row.original.merchant_name}</a>,
            size: 250
        },
        {
            header: "Merchant Id",
            filterable: true,
            accessorKey: "merchant_id",
            sticky: "left",
            cell: ({row}) => <a
                href={"https://www.amazon.com/sp?_encoding=UTF8&marketplaceID=ATVPDKIKX0DER&seller=" + row.original.merchant_id}
                target="_blank"
                rel="noopener noreferrer">{row.original.merchant_id}
            </a>
        },
        {header: "Price Violation", accessorKey: "price_count", sticky: "left", ...NUMBER_EXPRESSION_COLUMN},
        {header: "FBA Offers", accessorKey: "fba_count", sticky: "left", size: 100, ...NUMBER_EXPRESSION_COLUMN},
        {header: "Promo", accessorKey: "promo_count", sticky: "left", size: 100, ...NUMBER_EXPRESSION_COLUMN},
        {
            header: "KeHE Status", accessorKey: "kehe_status", size: 200,
            accessorFn: (row) => distributor_helper(row, "kehe_status_values"),
        },
        {
            header: "Palko Status", accessorKey: "palko_status", size: 200,
            accessorFn: (row) => distributor_helper(row, "palko_status_values")
        },
        {
            header: "Select Status", accessorKey: "select_status", size: 200,
            accessorFn: (row) => distributor_helper(row, "select_status_values")
        },
        {
            header: "Thresh. Status", accessorKey: "thresh_status", size: 200,
            accessorFn: (row) => distributor_helper(row, "threshold_status_values")
        },
        {
            header: "Direct Status", accessorKey: "direct_status", size: 200,
            accessorFn: (row) => distributor_helper(row, "direct_status_values")
        },
        {header: "Business Name", accessorKey: "business_name"},
        {header: "Address", accessorKey: "address"},
        {header: "Contact Name", accessorKey: "contact_name"},
        {header: "Phone", accessorKey: "contact_phone"},
        {header: "Email", accessorKey: "contact_email"}
    ];

    function transformData(data) {
        function remove_unwanted_characters(item) {
            if (item) {
                return item.replace(/[\n\r]/g, "");
            }
        }

        return data.map(item => ({
            "Merchant Name": item.merchant_name,
            "Merchant Id": item.merchant_id,
            "Price Violation": item.price_count.toString(),
            "FBA Offers": item.fba_count.toString(),
            "Promo": item.promo_count.toString(),
            "KeHE Status": distributor_helper(item, "kehe_status_values"),
            "Palko Status": distributor_helper(item, "palko_status_values"),
            "Select Status": distributor_helper(item, "select_status_values"),
            "Threshold Status": distributor_helper(item, "threshold_status_values"),
            "Direct Status": distributor_helper(item, "direct_status_values"),
            "Business Name": remove_unwanted_characters(item.business_name),
            "Address": remove_unwanted_characters(item.address),
            "Contact Name": remove_unwanted_characters(item.contact_name),
            "Phone": remove_unwanted_characters(item.contact_phone),
            "Email": remove_unwanted_characters(item.contact_email)
        }));

    }

    function downloadCsv(rows) {
        CsvDownload(transformData(rows.map(row => row.original)), `ClientDashboard_${brandName}.csv`)
    }

    return (
        <Page name={`Client Dashboard: ${brandName}`}
              breadCrumbs={<Breadcrumbs items={[{
                  label: "MAP Enforcement Dashboard",
                  to: '/mapEnforcementDashboard/'
              },
                  {label: "Client Management"}]}/>}
        >
            <div className="dates-container">
                <div>
                    <DateRangeInput
                        data-testid="date-range-input-wrapper"
                        startDate={startDate}
                        endDate={endDate}
                        onChange={handleDateChange}
                        maxDate={todayChicago().format("YYYY-MM-DD")}
                        numberOfMonths={1}
                        allowManualInput={true}
                    />
                </div>
                <div className='client-dashboard-submit-button-container'>
                    <button
                        className='button'
                        data-testid={'client-dashboard-start-date-button'}
                        onClick={() => getData()}
                    >
                        Submit
                    </button>
                </div>
            </div>
            <LoadingWrapper loading={loading}>
                {!_.isEmpty(data) && <ReactTable
                    filterable
                    data={data}
                    columns={customColumns}
                    initialState={{
                        pagination: {pageSize: 25},
                        sorting: [{id: "merchant_name", desc: false}]
                    }}
                >
                    {({rows}) => <div className='client-dashboard-download-csv-button-container'>
                        <button
                            className='button'
                            data-testid={'client-dashboard-download-csv-button'}
                            onClick={() => downloadCsv(rows)}
                        >
                            Export Csv
                        </button>
                    </div>}
                </ReactTable>}
            </LoadingWrapper>
        </Page>
    );
}

export default ClientDashboard;
